/**
 * @file index.tsx
 *
 * @fileoverview Renders a registration form based on the user role.
 */

import React, { useCallback, useEffect, useState } from 'react';
import EntityForm from '~/jobiqo/components/Form/EntityForm';
import GDPRConsentFormWrapper from '~/jobiqo/components/GDPRConsent/components/FormWrapper';
import { useGdprConsentContext } from '~/jobiqo/components/GDPRConsent/hooks/useGdprConsentContext';
import GdprConsentCheckboxes from '~/jobiqo/components/GDPRConsent/components/Checkboxes';
import { FrontendConfigContextType } from '~/jobiqo/context/frontendConfig';
import { getGroup } from '~/jobiqo/utils/config';
import { FormattedMessage, useIntl } from 'react-intl';
import RegisterCommonFields, {
  getCommonValidationSchema,
} from '../CommonFields';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import Captcha from '~/jobiqo/components/Captcha';
import { useCaptchaConfig } from '~/jobiqo/hooks/useCaptchaConfig';
import EducationIcon from '~/jobiqo/components/Icons/EducationIcon';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { useFormContext } from 'react-hook-form';
import OAuthButtons from '~/jobiqo/components/OAuthButtons';
import { LoadingDotsIcon } from '~/vcl/icons/LoadingDots';
import { OfficeIcon } from '~/vcl/icons/Office';
export const ANONYMOUS_REGISTRATION_LS_KEY = 'anonymous_registration_data';

export function ButtonsValidation({ onIsValidChange }) {
  const {
    formState: { isValid },
  } = useFormContext();

  useEffect(() => {
    onIsValidChange(isValid);
  }, [isValid]);

  return null;
}

interface RegistrationFormProps {
  /**
   * Function to call when the form is submitted.
   *
   * @param formData
   *  The form data.
   * @returns void
   */
  onSubmit: (formData: any) => void;
  /**
   * Whether the form is submitting the registration.
   *
   * @default "false"
   */
  isSubmittingRegister?: boolean;
  /**
   * Whether the user is applicant.
   */
  isApplicant: boolean;
  /**
   * Frontend configuration.
   */
  frontendConfig: FrontendConfigContextType;
}

export default function RegistrationForm({
  onSubmit,
  isSubmittingRegister = false,
  isApplicant,
  frontendConfig,
}: RegistrationFormProps) {
  const { APP, RESUME, ORGANIZATIONS } = frontendConfig;
  const { consentsValidation, consentInfos } = useGdprConsentContext();
  const intl = useIntl();
  const captchaConfig = useCaptchaConfig();
  const REGISTER_FORM = isApplicant
    ? { ...RESUME.REGISTER.register_applicant }
    : { ...ORGANIZATIONS.REGISTER.register_recruiter };
  const REGISTER_GROUP = getGroup(REGISTER_FORM);
  const roleRelatedCaptchaConfig = isApplicant
    ? captchaConfig.registerApplicant
    : captchaConfig.registerRecruiter;
  const [captchaValue, setCaptchaValue] = useState<string | boolean>(
    roleRelatedCaptchaConfig ? false : true,
  );
  const [formValid, setFormValid] = useState<boolean>(false);
  const registerFormKey = isApplicant
    ? 'register_applicant_form'
    : 'register_recruiter_form';
  const [
    anonymousRegistrationDataEmail,
    setAnonymousRegistrationDataEmail,
  ] = useState<string | null>(null);

  useEffect(() => {
    // Check if there is anonymous registration data in the local storage
    // and update the form inputs.
    const anonymousRegistrationData = sessionStorage.getItem(
      ANONYMOUS_REGISTRATION_LS_KEY,
    );
    if (anonymousRegistrationData) {
      setAnonymousRegistrationDataEmail(
        JSON.parse(anonymousRegistrationData).email,
      );
    }
  }, []);

  const onCaptchaChange = useCallback((value) => {
    setCaptchaValue(value);
  }, []);

  return (
    <div className={`register-form-${isApplicant ? 'applicant' : 'recruiter'}`}>
      <GDPRConsentFormWrapper consentPlacement="REGISTRATION">
        <EntityForm
          name={isApplicant ? 'profile' : 'group'}
          bundle={isApplicant ? 'resume' : 'organization'}
          fieldGroups={[REGISTER_GROUP]}
          additionalFieldList={['email', 'password']}
          extraInitialValues={{
            ...consentsValidation.initialValues,
            ...(anonymousRegistrationDataEmail
              ? { email: anonymousRegistrationDataEmail }
              : {}),
          }}
          extraValidationSchema={{
            ...getCommonValidationSchema(intl),
            ...consentsValidation.schema,
          }}
          formGroupsChildren={{
            [registerFormKey]: ({}) => (
              <>
                <RegisterCommonFields />
                <Spacing size="large" />
                <GdprConsentCheckboxes />
                {roleRelatedCaptchaConfig && (
                  <Spacing marginTop={5}>
                    <Captcha
                      onCaptchaChange={onCaptchaChange}
                      siteKey={captchaConfig.siteKey}
                    />
                  </Spacing>
                )}
              </>
            ),
          }}
          renderButtons={({}) => {
            return (
              <>
                <Button
                  block={true}
                  type="submit"
                  buttonStyle="primary"
                  data-test-id="register-submit"
                  disabled={
                    isSubmittingRegister ||
                    !formValid ||
                    APP.READ_ONLY_MODE ||
                    !captchaValue
                  }
                >
                  {isSubmittingRegister ? (
                    <LoadingDotsIcon title="Loading" />
                  ) : (
                    <>
                      {isApplicant ? (
                        <EducationIcon title="Register" aria-hidden={true} />
                      ) : (
                        <OfficeIcon title="Register" aria-hidden={true} />
                      )}
                      <span>
                        <FormattedMessage
                          id="register"
                          defaultMessage="Register"
                        />
                      </span>
                    </>
                  )}
                </Button>
                <Spacing />
                {!APP.READ_ONLY_MODE ? (
                  <OAuthButtons
                    gdpr={consentsValidation}
                    consentInfos={consentInfos}
                    role={isApplicant ? 'applicant' : 'recruiter'}
                  />
                ) : null}
              </>
            );
          }}
          onSubmit={(formData) =>
            onSubmit({
              ...formData,
              captchaToken:
                typeof captchaValue === 'string' ? captchaValue : '',
            })
          }
        >
          <ButtonsValidation
            onIsValidChange={(isValid) => setFormValid(isValid)}
          />
        </EntityForm>
      </GDPRConsentFormWrapper>
    </div>
  );
}
